import { useState } from 'preact/hooks'

/**
 * Entsperrt den Schlüsselspeicher.
 *
 * **Stand:** Passphrase-Weg. Der geplante Hauptweg ist die WebAuthn-PRF-Extension
 * (ADR-004, SECURITY.md §3.5) — Fingerabdruck auflegen, fertig, kein Passwort.
 * Der Server merkt sich pro Passkey schon, ob PRF unterstützt wird
 * (`credential.prf_supported`); die Client-Seite fehlt noch. Bis dahin ist das
 * hier der ehrliche Zwischenstand, kein Ersatz.
 */
export function Entsperren({ neu, onEntsperrt }: {
  neu: boolean
  onEntsperrt: (passphrase: string) => Promise<void>
}) {
  const [passphrase, setPassphrase] = useState('')
  const [wiederholung, setWiederholung] = useState('')
  const [busy, setBusy] = useState(false)
  const [fehler, setFehler] = useState<string | null>(null)

  async function absenden(e: Event) {
    e.preventDefault()
    setFehler(null)

    if (neu && passphrase !== wiederholung) {
      setFehler('Die beiden Eingaben stimmen nicht überein.')
      return
    }
    if (passphrase.length < 12) {
      setFehler('Bitte mindestens 12 Zeichen — ein kurzer Satz funktioniert gut.')
      return
    }

    setBusy(true)
    try {
      await onEntsperrt(passphrase)
    } catch (e) {
      setFehler(e instanceof Error ? e.message : 'Das hat nicht geklappt.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div class="fh-anmelden">
      <h1 class="fh-h1">{neu ? 'Deine Nachrichten schützen' : 'Nachrichten entsperren'}</h1>

      {neu ? (
        <p class="fh-muted">
          Wähle einen Satz, den nur du kennst. Er schützt deine Nachrichten auf
          diesem Gerät — auch vor dem Server. Ein Satz mit vier Wörtern ist
          sicherer und leichter zu merken als ein kompliziertes Passwort.
        </p>
      ) : (
        <p class="fh-muted">
          Gib den Satz ein, mit dem du deine Nachrichten geschützt hast.
        </p>
      )}

      <form class="fh-card fh-anmelden__formular" onSubmit={(e) => void absenden(e)}>
        <label class="fh-feld">
          <span>{neu ? 'Dein Satz' : 'Satz'}</span>
          <input
            type="password" required autofocus
            autocomplete={neu ? 'new-password' : 'current-password'}
            value={passphrase}
            placeholder="zum Beispiel: mein blauer Stuhl knarzt"
            onInput={(e) => setPassphrase((e.target as HTMLInputElement).value)}
          />
        </label>

        {neu && (
          <label class="fh-feld">
            <span>Noch einmal</span>
            <input
              type="password" required autocomplete="new-password" value={wiederholung}
              onInput={(e) => setWiederholung((e.target as HTMLInputElement).value)}
            />
          </label>
        )}

        {fehler && <p class="fh-hinweis fh-hinweis--fehler" role="alert">{fehler}</p>}

        <button class="fh-btn" type="submit" disabled={busy}>
          {busy ? 'Einen Moment…' : neu ? 'Weiter' : 'Entsperren'}
        </button>
      </form>

      {neu && (
        <p class="fh-hinweis fh-hinweis--info">
          <span aria-hidden="true">ℹ️</span>
          <span>
            Wenn du diesen Satz vergisst und alle deine Geräte verlierst, sind
            deine alten Nachrichten weg — auch wir kommen dann nicht mehr daran.
            Im nächsten Schritt bekommst du dafür ein Notfall-Blatt.
          </span>
        </p>
      )}
    </div>
  )
}
